import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import {
  bedmapsListingIds, deleteNetworkContact, listNetworkContacts, listPartners, loadResearch, partnerStatus, saveNetworkContact, setBedmapsListings,
} from './adminData.js';
import { ListingSwitch, PartnerSwitch, StagePanel, StageSwitch, useProviderStages } from './ProviderStages.jsx';
import { SortableTable } from './SortableTable.jsx';

const list = (value) => (Array.isArray(value) ? value : []);
const firstOf = (value) => (Array.isArray(value) ? value.filter(Boolean)[0] : value) || '';
/** A research contact is a name, or `{ name, role }`: "Innovate UK Business Growth, Regional Innovation Adviser". */
const personOf = (value) => (value && typeof value === 'object' ? [value.name, value.role].filter(Boolean).join(', ') : String(value || ''));

/** Every row in one shape: name, kind, area, contact, email, phone, website, and `own` when it is one of the Network's own contacts. */
const row = (fields) => {
  const filled = { kind: '', area: '', contact_name: '', email: '', phone: '', website: '', note: '', own: false, ...fields };
  // Anything that isn't words (a list or an object from a research file) is turned into words, so a cell can never break the page.
  for (const key of ['name', 'kind', 'area', 'contact_name', 'email', 'phone', 'website', 'note']) {
    const value = filled[key];
    if (value !== null && value !== undefined && typeof value !== 'string') filled[key] = Array.isArray(value) ? value.map(personOf).join(', ') : personOf(value);
  }
  return filled;
};

/**
 * The kinds of institution we work with, in the toggle's order. Each reads its contacts from where they are already kept (`load`),
 * from the Network's own list (`own`, which admins add to here), or both. `listing` says which of them can be ticked to show on
 * BedMaps' public pages.
 */
const types = [
  // Only councils we have an established line with, kept by hand here; every council is in the Councils tab.
  { key: 'councils', name: 'Councils', own: 'councils' },
  {
    // Only partners. A provider who has only replied waits in Outreach until they are switched on there.
    key: 'providers', name: 'Housing providers', listing: 'partner',
    load: async () => list((await listPartners(supabase))?.rows).filter((partner) => partner.is_partner).map((partner) => row({
      id: partner.provider_id, name: partner.organisation, kind: partnerStatus(partner.status).label,
      area: list(partner.councils).map((council) => council.name).join(', '),
      contact_name: [partner.contact_name, partner.contact_role].filter(Boolean).join(', '),
      email: partner.contact_email || partner.referral_email || partner.email, phone: partner.contact_phone || partner.phone, website: partner.website,
      listable: true,
    })),
  },
  { key: 'community', name: 'Community organisations', own: 'community', listing: 'contact' },
  { key: 'public', name: 'Public services', own: 'public_services', listing: 'contact' },
  { key: 'third-party', name: 'Third-party providers', own: 'third_party', listing: 'contact' },
  {
    key: 'investors', name: 'Investors',
    load: async () => list(await loadResearch(supabase, 'investors')).map((investor) => row({
      id: investor.id, name: investor.name, kind: investor.type, area: investor.hq, email: firstOf(investor.emails), website: investor.website,
    })),
  },
  {
    key: 'funders', name: 'Funders',
    load: async () => list(await loadResearch(supabase, 'funding')).map((fund) => row({
      id: fund.id, name: fund.name, kind: fund.instrument || fund.type, area: fund.hq, contact_name: personOf(firstOf(fund.contacts)),
      email: firstOf(fund.emails), website: fund.website || fund.apply_url,
    })),
  },
  { key: 'sponsors', name: 'Sponsors', own: 'sponsors' },
];

const fields = [
  ['name', 'Name', 'text', 'Ladywood Community Centre'],
  ['kind', 'Kind', 'text', 'Community centre'],
  ['area', 'Area', 'text', 'Birmingham'],
  ['contact_name', 'Contact', 'text', 'Sam Patel, Centre manager'],
  ['email', 'Email', 'email', 'hello@example.org'],
  ['phone', 'Phone', 'tel', '0121 000 0000'],
  ['website', 'Website', 'url', 'https://example.org'],
  ['address', 'Address', 'text', '1 Ladywood Road, Birmingham'],
  ['postcode', 'Postcode', 'text', 'B16 8AA'],
];
const blankForm = Object.fromEntries([...fields.map(([key]) => [key, '']), ['description', ''], ['note', '']]);

const webLink = (url) => {
  if (!url) return '–';
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  let label = url;
  try { label = new URL(href).hostname.replace(/^www\./, ''); } catch { /* shown as typed */ }
  return <a className="admin-ext" href={href} target="_blank" rel="noreferrer">{label}</a>;
};

/** Adds a contact to the Network's own list, or changes one (`contact`). */
function ContactForm({ type, contact, busy, error, onSave, onCancel }) {
  const [form, setForm] = useState(() => (contact ? Object.fromEntries(Object.keys(blankForm).map((key) => [key, contact[key] ?? ''])) : blankForm));
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const title = contact ? `Change ${contact.name}` : `Add to ${type.name.toLowerCase()}`;
  return (
    <form className="admin-partner-form" aria-label={title} onSubmit={(event) => { event.preventDefault(); onSave(form); }}>
      <h3 className="admin-subsection">{title}</h3>
      <div className="admin-partner-grid">
        {fields.map(([key, label, kind, placeholder]) => (
          <label key={key}>{label}
            <input type={kind} value={form[key]} onChange={set(key)} placeholder={placeholder} maxLength={200} required={key === 'name'}/>
          </label>
        ))}
      </div>
      <label className="admin-partner-wide">What they do
        <textarea rows={2} value={form.description} onChange={set('description')} maxLength={500} placeholder="Shown on BedMaps once ticked"/>
      </label>
      <label className="admin-partner-wide">Note
        <textarea rows={2} value={form.note} onChange={set('note')} maxLength={1000} placeholder="How we know them, what we have agreed"/>
      </label>
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="admin-head-actions">
        <button type="submit" className="button button-dark" disabled={busy}>{contact ? 'Save changes' : 'Add'}</button>
        <button type="button" className="button button-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/**
 * The Network tab: the institutions we work with, one kind at a time (`show`: councils, housing providers, community
 * organisations, public services, third-party providers, investors, funders, sponsors), as a table of real contacts with a search.
 * Housing providers who replied to Naima, investors and funders come from their own lists; councils we are in touch with,
 * community organisations, public services, third parties and sponsors are kept here, where admins add, change and remove them. `onShow(key)` picks the kind.
 */
export default function NetworkTab({ show, onShow }) {
  const type = types.find((item) => item.key === show) || types[0];
  const [state, setState] = useState({ rows: null, error: '' });
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  // Who is switched on for BedMaps, whether that could be read, and whether "show everyone this search found" is waiting for a yes.
  const [listed, setListed] = useState(() => new Set());
  const [ticksRead, setTicksRead] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const latest = useRef(0);

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    setState({ rows: null, error: '' });
    try {
      const [shared, own, ticked] = await Promise.all([
        type.load ? type.load() : [],
        type.own ? listNetworkContacts(supabase, type.own).then((rows) => list(rows).map((contact) => row({ ...contact, own: true, listable: Boolean(type.listing) }))) : [],
        // The list matters more than the switches, so failing to read them only hides the switches.
        type.listing === 'contact' ? bedmapsListingIds(supabase).catch(() => null) : null,
      ]);
      if (ticket !== latest.current) return;
      setListed(new Set(list(ticked?.contacts)));
      setTicksRead(Boolean(ticked));
      setState({ rows: [...own, ...shared], error: '' });
    } catch (cause) {
      if (ticket === latest.current) setState({ rows: [], error: cause.message || 'Could not load these contacts. Please try again.' });
    }
  }, [type]);
  useEffect(() => {
    setQuery('');
    setEditing(null);
    setRemoving('');
    setConfirmingAll(false);
    void load();
    return () => { latest.current += 1; };
  }, [load]);

  // Housing providers have two switches of their own. Taking away partner takes them off this list, so it is read again.
  const stages = useProviderStages({ enabled: type.listing === 'partner', onChanged: load });

  const save = async (form) => {
    setBusy(true);
    setFormError('');
    try {
      await saveNetworkContact(supabase, editing === 'new' ? null : editing.id, type.own, form);
      setEditing(null);
      await load();
    } catch (cause) {
      setFormError(cause.message || 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (contact) => {
    setBusy(true);
    try {
      await deleteNetworkContact(supabase, contact.id);
      setRemoving('');
      await load();
    } catch (cause) {
      setState((current) => ({ ...current, error: cause.message || 'Could not remove. Please try again.' }));
    } finally {
      setBusy(false);
    }
  };

  const setListing = async (items, show) => {
    const ids = items.map((item) => item.id);
    setBusy(true);
    try {
      await setBedmapsListings(supabase, type.listing, ids, show);
      setListed((current) => {
        const next = new Set(current);
        for (const id of ids) { if (show) next.add(id); else next.delete(id); }
        return next;
      });
      setConfirmingAll(false);
    } catch (cause) {
      setState((current) => ({ ...current, error: cause.message || 'Could not change BedMaps. Please try again.' }));
    } finally {
      setBusy(false);
    }
  };

  const words = query.trim().toLowerCase();
  const rows = (state.rows || []).filter((item) => !words
    || [item.name, item.kind, item.area, item.contact_name, item.email].some((value) => String(value || '').toLowerCase().includes(words)));
  const listable = ticksRead && type.listing === 'contact' ? rows.filter((item) => item.listable) : [];
  const columns = [
    { key: 'name', label: 'Name', value: (item) => item.name, cell: (item) => item.name },
    { key: 'kind', label: 'Kind', value: (item) => item.kind, cell: (item) => item.kind || '–' },
    { key: 'area', label: 'Area', value: (item) => item.area, cell: (item) => item.area || '–' },
    { key: 'contact', label: 'Contact', value: (item) => item.contact_name, cell: (item) => item.contact_name || '–' },
    { key: 'email', label: 'Email', value: (item) => item.email, cell: (item) => (item.email ? <a href={`mailto:${item.email}`}>{item.email}</a> : '–') },
    { key: 'phone', label: 'Phone', value: (item) => item.phone, cell: (item) => (item.phone ? <a href={`tel:${item.phone.replace(/\s+/g, '')}`}>{item.phone}</a> : '–') },
    { key: 'website', label: 'Website', value: (item) => item.website, cell: (item) => webLink(item.website) },
    ...(type.listing === 'contact' && ticksRead ? [{
      key: 'bedmaps', label: 'On BedMaps', first: 'desc', value: (item) => (listed.has(item.id) ? 1 : 0),
      cell: (item) => (item.listable ? <StageSwitch label={`Show ${item.name} on BedMaps`} on={listed.has(item.id)} disabled={busy} onChange={(on) => setListing([item], on)}/> : null),
    }] : []),
    ...(type.listing === 'partner' && stages.ready ? [
      { key: 'partner', label: 'Partner', value: (item) => (stages.partners.get(item.id)?.is_partner ? 1 : 0), cell: (item) => <PartnerSwitch stages={stages} id={item.id} name={item.name}/> },
      { key: 'bedmaps', label: 'On BedMaps', first: 'desc', value: (item) => (stages.listed.has(item.id) ? 1 : 0), cell: (item) => <ListingSwitch stages={stages} id={item.id} name={item.name}/> },
    ] : []),
    ...(type.own ? [{
      key: 'actions', label: '', value: () => '',
      cell: (item) => (!item.own ? null : removing === item.id ? (
        <span className="admin-head-actions">
          <button type="button" className="admin-link" disabled={busy} onClick={() => remove(item)}>Yes, remove</button>
          <button type="button" className="admin-link" onClick={() => setRemoving('')}>Keep</button>
        </span>
      ) : (
        <span className="admin-head-actions">
          <button type="button" className="admin-link" aria-label={`Change ${item.name}`} onClick={() => { setFormError(''); setEditing(item); }}>Change</button>
          <button type="button" className="admin-link" aria-label={`Remove ${item.name}`} onClick={() => setRemoving(item.id)}>Remove</button>
        </span>
      )),
    }] : []),
  ];

  return (
    <>
      <div className="mod-filters">
        <div className="segmented seg-wrap" role="group" aria-label="Who">
          {types.map((item) => (
            <button key={item.key} type="button" aria-pressed={item.key === type.key} className={item.key === type.key ? 'active' : ''} onClick={() => onShow(item.key)}>{item.name}</button>
          ))}
        </div>
      </div>
      <div className="mod-filters">
        <input type="search" aria-label={`Search ${type.name.toLowerCase()}`} placeholder="Search by name, kind, area or email" value={query} onChange={(event) => { setQuery(event.target.value); setConfirmingAll(false); }}/>
        {type.own && !editing && <button type="button" className="button button-dark" onClick={() => { setFormError(''); setEditing('new'); }}>{`Add to ${type.name.toLowerCase()}`}</button>}
        {/* Showing goes on a public website, so a whole search needs a yes first. Hiding doesn't. */}
        {words && listable.length > 0 && (confirmingAll ? (
          <span className="admin-head-actions">
            <button type="button" className="admin-link" disabled={busy} onClick={() => setListing(listable, true)}>{`Yes, show these ${listable.length}`}</button>
            <button type="button" className="admin-link" onClick={() => setConfirmingAll(false)}>Keep as they are</button>
          </span>
        ) : (
          <span className="admin-head-actions">
            {listable.some((item) => !listed.has(item.id)) && <button type="button" className="admin-link" disabled={busy} onClick={() => setConfirmingAll(true)}>{`Show these ${listable.length} on BedMaps`}</button>}
            {listable.some((item) => listed.has(item.id)) && <button type="button" className="admin-link" disabled={busy} onClick={() => setListing(listable, false)}>{`Hide these ${listable.length} from BedMaps`}</button>}
          </span>
        ))}
      </div>
      {editing && (
        <ContactForm key={editing === 'new' ? 'new' : editing.id} type={type} contact={editing === 'new' ? null : editing} busy={busy} error={formError}
          onSave={save} onCancel={() => setEditing(null)}/>
      )}
      {state.error && <p className="mod-error" role="alert">{state.error}</p>}
      {type.listing === 'partner' && <StagePanel stages={stages}/>}
      {state.rows === null ? <p>{`Loading ${type.name.toLowerCase()}…`}</p> : (
        <>
          <p className="mod-note">{words ? `Showing ${rows.length} of ${state.rows.length}` : `${state.rows.length} in the list`}</p>
          {type.listing === 'contact' && (
            <p className="mod-note">{ticksRead ? `${state.rows.filter((item) => item.listable && listed.has(item.id)).length} on BedMaps` : "The BedMaps switches aren't available right now."}</p>
          )}
          {type.listing === 'partner' && stages.ready && <p className="mod-note">{`${state.rows.filter((item) => stages.listed.has(item.id)).length} on BedMaps`}</p>}
          {rows.length === 0
            ? <p className="admin-empty">{state.rows.length === 0 ? `No ${type.name.toLowerCase()} yet.` : 'Nothing matches that search.'}</p>
            : <SortableTable columns={columns} rows={rows} rowKey={(item) => `${item.own ? 'own' : 'list'}-${item.id}`}/>}
        </>
      )}
    </>
  );
}
